import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import {LoadingButton} from '@mui/lab';
import {AutomationFlow, NodeType} from 'crqlar-react-common/dist/models/automation/automation';
import {useTranslation} from 'react-i18next';
import {getNodeColor, getNodeDisplay, isTriggerNode} from '../config/nodeCategories';

export const getTriggerNodes = (automation: AutomationFlow) => (
    automation.nodes?.filter(node => isTriggerNode(node.type)) ?? []
);

export function AutomationNameCell({automation}: {automation: AutomationFlow}) {
    return (
        <Box sx={{py: 0.5}}>{automation.name ?? null}</Box>
    );
}

export function AutomationTriggersCell({automation}: {automation: AutomationFlow}) {
    const {t} = useTranslation();
    const triggerTypes = Array.from(new Set(getTriggerNodes(automation).map(node => node.type)));

    // No trigger yet: an empty cell, no "No triggers" chip.
    if (!triggerTypes.length) return null;

    return (
        <Stack direction="row" gap={0.5} flexWrap="wrap" alignItems="center" sx={{py: 0.5}}>
            {triggerTypes.map(triggerType => {
                const {icon, label} = getNodeDisplay(triggerType);
                return (
                    <Tooltip key={triggerType} title={t(label)}>
                        <Box component="span" tabIndex={0} aria-label={t(label)} sx={{display: 'inline-flex', p: 0.5,
                            color: getNodeColor(triggerType), '& .MuiSvgIcon-root': {fontSize: 20}}}>
                            {icon}
                        </Box>
                    </Tooltip>
                );
            })}
        </Stack>
    );
}

/** The first manual trigger with a step behind it, else the first one. */
export const getListManualTrigger = (automation: AutomationFlow) => {
    const manualTriggers = automation.nodes?.filter(node => node.type === NodeType.MANUAL_TRIGGER) ?? [];
    return manualTriggers.find(node => automation.connections?.some(connection => connection.sourceNodeId === node.id))
        ?? manualTriggers[0];
};

/** Rendered only for a flow that can run now; one that cannot gets no button, not a disabled one (AUTOMATIONS_UI.md, Decisions). */
export function AutomationRunCell({isRunning, disabled, onRequestRun}: {
    isRunning: boolean;
    disabled: boolean;
    onRequestRun: () => void;
}) {
    const {t} = useTranslation();

    return (
        // Stops a click on the button (or beside it while busy) from opening the row.
        <Box component="span" sx={{display: 'inline-flex', py: 0.5}} onClick={event => event.stopPropagation()}>
            <LoadingButton size="small" variant="outlined" loading={isRunning} disabled={disabled} onClick={onRequestRun}>
                {t('Run for everyone')}
            </LoadingButton>
        </Box>
    );
}
